import React from 'react';
import { get } from 'lodash-es';

import { parseContent } from 'client/data/cms/content';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { PageModel } from 'client/data/models/page';
import { CmsModel } from 'client/data/models/cms';
import {
  buildEvTestedDataPath,
  buildFuelCostsPath,
  buildVehiclePath,
  VehicleModel,
} from 'client/data/models/vehicle-v2';
import { buildMpgStylesPath, MpgModel } from 'client/data/models/mpg';
import { buildAllPhotosPath, PhotoflipperModel } from 'client/data/models/photoflipper';
import { ComparatorModel } from 'client/data/models/comparator';
import { buildMMYSubmodelTrimPhotosPathWithParams, PhotosModel } from 'client/data/models/photos';

import { DEFAULT_PERSONALIZATION_PARAMS } from 'site-modules/shared/constants/range-and-cost';

import { setCriticalCss } from 'client/actions/critical-css';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { getPageCategory, getPageName } from 'site-modules/editorial/pages/type/article-definition';
import { getTypeArticleConfigPath, getTypeArticleContentPath } from 'site-modules/editorial/utils/content-path';
import { getSeoObject } from 'site-modules/editorial/pages/type/seo';
import { IncentivesElectricModel, IncentivesElectricPaths } from 'client/data/models/incentives-electric';

import { TypeBuyingGuide } from './type-buying-guide';

export const getPageOptions = ({ type, articleId, chunkName }) => ({
  name: getPageName(type),
  category: getPageCategory(type),
  chunkName,
  adsCustomTargeting: {
    sect1: articleId.replace(/[^a-zA-Z0-9]/g, ''),
    sect2: 'articles',
  },
  seo: (state, props) => ({
    ...getSeoObject(
      state,
      getTypeArticleContentPath(type, articleId),
      getTypeArticleConfigPath(type),
      props.location.pathname
    ),
  }),
  preload: store => {
    const preloader = new ModelPreloader(store);
    const articlePath = `${getTypeArticleContentPath(type, articleId)}`;
    preloader.resolve(`content["${articlePath}"]`, CmsModel);

    return preloader.load().then(modelState => {
      const content = parseContent(modelState.get(`content["${articlePath}"]`, CmsModel));
      const mainContent = content.child('main');

      const evIncentives = mainContent.child('ev-incentives');
      const evIncentivesVehicle = get(evIncentives, 'vehicles[0]');
      if (evIncentivesVehicle) {
        // EvIncentives Module
        preloader.resolve(
          IncentivesElectricPaths.getIncentivesGuidePath({
            makeSlug: evIncentivesVehicle.make,
            modelSlug: evIncentivesVehicle.model,
          }),
          IncentivesElectricModel
        );
      }

      const evMpg = mainContent.child('ev-mpg');
      const evMpgVehicle = get(evMpg, 'vehicles[0]');
      if (evMpgVehicle) {
        // EvMpg Module
        preloader.resolve(buildVehiclePath(evMpgVehicle), VehicleModel);
        preloader.resolve(buildMpgStylesPath(evMpgVehicle), MpgModel);
      }

      const vehiclesCompareTable = mainContent.child('compare-table').children();
      if (vehiclesCompareTable.length) {
        const columns = vehiclesCompareTable.map(({ vehicles }) => {
          const styleId = vehicles[0].style;
          preloader.resolve(styleId && `styles.${styleId}`, VehicleModel);
          return styleId;
        });
        preloader.resolve(`editorialReview["${columns.join()}"]`, ComparatorModel);
      }

      return preloader.load().then(modelStateObj => {
        if (evMpgVehicle) {
          const makeModelSubmodelYear = modelStateObj.get(buildVehiclePath(evMpgVehicle), VehicleModel);
          const styles = modelStateObj.get(buildMpgStylesPath(evMpgVehicle), MpgModel);
          const currentStyle = get(styles, '[0]');

          preloader.resolve(
            buildAllPhotosPath({
              ...evMpgVehicle,
              submodel: makeModelSubmodelYear.submodels.name,
              pageNum: 1,
              imageSize: 600,
            }),
            PhotoflipperModel
          );
          preloader.resolve(buildEvTestedDataPath([get(currentStyle, 'id')]), VehicleModel);
          preloader.resolve(
            buildFuelCostsPath(get(currentStyle, 'id'), {
              milesPerYear: DEFAULT_PERSONALIZATION_PARAMS.milesPerYear,
              cityPercents: DEFAULT_PERSONALIZATION_PARAMS.cityPercents,
            }),
            VehicleModel
          );
        }

        if (vehiclesCompareTable.length) {
          vehiclesCompareTable.forEach(({ vehicles }) => {
            const styleId = vehicles[0].style;
            const vehicle = modelState.get(`styles.${styleId}`, VehicleModel);
            const { makeSlug, modelSlug, year, submodels, trimName } = vehicle;

            preloader.resolve(
              buildMMYSubmodelTrimPhotosPathWithParams({
                make: makeSlug,
                model: modelSlug,
                submodel: get(submodels, 'name'),
                trim: trimName,
                year,
                width: '500',
              }),
              PhotosModel
            );
          });
        }

        const assetsToPreload = [{ href: content.metadata('heroImage').value(), as: 'image' }];
        preloader.set('server.preloadedAssets', PageModel, assetsToPreload);

        store.dispatch(setCriticalCss({ 'type-buying-guide-article': type }));

        return preloader.load();
      });
    });
  },
  component: () => <TypeBuyingGuide type={type} articleId={articleId} />,
});

export default params => pageDefinition(getPageOptions(params));
